import { ChevronDownIcon } from '@heroicons/react/20/solid';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { usePatchMatterMutation } from '@/services/api/mattersService';
import { useCreateStaffNoteMutation } from '@/services/api/staffNotesService';
import { MatterStatus } from '@/services/types/client-intake-types';
import { MatterListItem } from '@/services/types/matter-types';

import {
  isTerminalMatterStatus,
  USER_SELECTABLE_MATTER_STATUSES,
} from '../config/matterStatusConfig';

interface BulkChangeStatusActionProps {
  selectedMatters: MatterListItem[];
  onComplete: () => void;
}

export const BulkChangeStatusAction: React.FC<BulkChangeStatusActionProps> = ({
  selectedMatters,
  onComplete,
}) => {
  const [pendingStatus, setPendingStatus] = React.useState<MatterStatus | null>(
    null,
  );
  const [note, setNote] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [patchMatter] = usePatchMatterMutation();
  const [createStaffNote] = useCreateStaffNoteMutation();

  const isNoteModalOpen = pendingStatus !== null;
  const canSubmit = note.trim() !== '';

  const handleCloseModal = () => {
    if (!isSubmitting) {
      setPendingStatus(null);
      setNote('');
    }
  };

  const handleBulkStatusChange = React.useCallback(
    async (newStatus: MatterStatus, noteContent?: string) => {
      setIsSubmitting(true);

      const promises = selectedMatters.map(async (matter) => {
        // For terminal statuses, create staff note first
        if (noteContent) {
          await createStaffNote({
            matterId: matter.id,
            note: { content: noteContent },
          }).unwrap();
        }

        // Then update status
        return patchMatter({
          matter_id: matter.id,
          patch: { status: newStatus },
        }).unwrap();
      });

      const results = await Promise.allSettled(promises);
      const successful = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;

      if (failed === 0) {
        toast.success(
          `Successfully updated ${successful} matter${successful !== 1 ? 's' : ''}`,
        );
      } else if (successful > 0) {
        toast.warning(
          `Updated ${successful} matter${successful !== 1 ? 's' : ''}, but ${failed} failed`,
        );
      } else {
        toast.error('Failed to update matters');
      }

      setIsSubmitting(false);
      setPendingStatus(null);
      setNote('');
      onComplete();
    },
    [selectedMatters, patchMatter, createStaffNote, onComplete],
  );

  const handleStatusSelect = (status: MatterStatus) => {
    if (isTerminalMatterStatus(status)) {
      // Terminal status - need to collect note first
      setPendingStatus(status);
    } else {
      // Non-terminal status - proceed directly
      handleBulkStatusChange(status);
    }
  };

  const handleConfirmWithNote = async () => {
    if (!pendingStatus || !canSubmit) return;
    await handleBulkStatusChange(pendingStatus, note.trim());
  };

  // Handle Cmd/Ctrl+Enter to submit
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.key === 'Enter' &&
      (e.metaKey || e.ctrlKey) &&
      canSubmit &&
      !isSubmitting
    ) {
      e.preventDefault();
      handleConfirmWithNote();
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            disabled={isSubmitting}
          >
            Change Status
            <ChevronDownIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
          {USER_SELECTABLE_MATTER_STATUSES.map((status) => (
            <DropdownMenuItem
              key={status}
              onClick={() => handleStatusSelect(status)}
            >
              {status}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={isNoteModalOpen}
        onOpenChange={(open) => !open && handleCloseModal()}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Status Change Note</DialogTitle>
            <DialogDescription>
              You are changing <strong>{selectedMatters.length}</strong> matter
              {selectedMatters.length !== 1 ? 's' : ''} to{' '}
              <strong>{pendingStatus}</strong>. Please provide a reason that
              will appear in each matter&apos;s activity feed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="bulk-status-change-note">
                Note <span className="text-red-500 dark:text-red-400">*</span>
              </Label>
              <Textarea
                id="bulk-status-change-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter the reason for this status change..."
                rows={4}
                className="resize-none"
                aria-required
                aria-invalid={!note.trim()}
                autoFocus
              />
              {!note.trim() && (
                <p className="text-xs text-muted-foreground">
                  A note is required to change the status to {pendingStatus}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmWithNote}
              disabled={!canSubmit || isSubmitting}
            >
              {isSubmitting
                ? 'Updating...'
                : `Update ${selectedMatters.length} Matter${selectedMatters.length !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
